import { useQuery } from '@apollo/client';
import { useContext, useMemo } from 'react';
import { gqlTypes } from '../../types/gqlTypes';
import { chipDetailsQuery } from '../ChipDetails/ChipDetails.graphql';
import RegisterChipContextV2 from './RegisterChipContextV2';

type ChipDetailsChip = NonNullable<gqlTypes.chipDetails['chip']>;

/**
 * An assigned chip carries its pet directly; a chip still awaiting a transfer carries it under
 * `transfer`. Both arrive at the post-registration screens.
 */
function petFromChip(chip: ChipDetailsChip | undefined) {
  if (chip?.__typename === 'AssignedChip') {
    return chip.pet ?? undefined;
  }
  if (chip?.__typename === 'StrangerChip') {
    return chip.transfer?.pet ?? undefined;
  }
  return undefined;
}

/**
 * The pet of a chip the registrant now owns. A chip awaiting a transfer has a pet too, but it is still
 * in its owner's household, where the registrant can't read it.
 */
function assignedPetIdFromChip(chip: ChipDetailsChip | undefined) {
  if (chip?.__typename === 'AssignedChip') {
    return chip.pet?.id ?? undefined;
  }
  return undefined;
}

/**
 * The rescue's give-with-adoption offer, present only when the chip's reseller is an enrolled rescue.
 * Only an assigned chip exposes it, so a pending transfer never has one.
 */
function gwaOfferFromChip(chip: ChipDetailsChip | undefined) {
  if (chip?.__typename === 'AssignedChip') {
    return chip.gwaOffer ?? null;
  }
  return null;
}

/**
 * The contact details captured during registration, which already hold a full shipping address:
 * name, both lines, city, state, zipcode, country and phone. Only an assigned chip has them.
 */
function contactInfoFromChip(chip: ChipDetailsChip | undefined) {
  if (chip?.__typename === 'AssignedChip') {
    return chip.latestContactHistoryEntry?.contactInfo ?? undefined;
  }
  return undefined;
}

/**
 * The pet and offer behind the chip just registered.
 *
 * This is the derivation `SuccessStepV2` does inline, extracted so the bag and checkout screens read
 * a registration the same way instead of each re-deriving it. `SuccessStepV2` should adopt this too;
 * left to a follow-up to keep this diff reviewable.
 */
export function useRegisteredChip() {
  const { registerState } = useContext(RegisterChipContextV2);
  const { chipId } = registerState;

  const { data, loading } = useQuery<gqlTypes.chipDetails>(chipDetailsQuery, {
    variables: { id: chipId },
    skip: !chipId,
    fetchPolicy: 'network-only',
  });

  return useMemo(() => {
    return {
      loading,
      chipId,
      petInfo: petFromChip(data?.chip ?? undefined),
      assignedPetId: assignedPetIdFromChip(data?.chip ?? undefined),
      gwaOffer: gwaOfferFromChip(data?.chip ?? undefined),
      contactInfo: contactInfoFromChip(data?.chip ?? undefined),
    };
  }, [data, loading, chipId]);
}
